import { useState } from 'react'
import { ChevronLeft, ChevronRight, History } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useAuditLog } from '@/features/collaboration/use-collaboration'
import type { AuditEventType, AuditLogEntry } from '@/api/types'

interface Props {
  projectId: string
  trigger?: React.ReactNode
}

const PAGE_SIZE = 20

const verbFor = (eventType: AuditEventType): string => {
  switch (eventType) {
    case 'PROJECT_CREATED':
      return 'created the project'
    case 'TITLE_CHANGED':
      return 'renamed the project'
    case 'DESCRIPTION_CHANGED':
      return 'updated the description'
    case 'VISIBILITY_CHANGED':
      return 'changed visibility'
    case 'HERO_CHANGED':
      return 'updated the hero image'
    case 'PROJECT_PUBLISHED':
      return 'published a new version'
    case 'PROJECT_DELETED':
      return 'deleted the project'
    case 'INVITE_SENT':
      return 'invited a collaborator'
    case 'INVITE_ACCEPTED':
      return 'accepted an invitation'
    case 'INVITE_DECLINED':
      return 'declined an invitation'
    case 'INVITE_REVOKED':
      return 'revoked a collaborator'
    case 'ROLE_CHANGED':
      return "changed a collaborator's role"
    default:
      return 'changed something'
  }
}

const formatRelative = (iso: string): string => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 14) return `${days}d ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Phase 18 — Owner + Editor history view. Server-paginated newest-first; prev/next, no infinite
 * scroll. Sized for projects with hundreds of structural events at most.
 */
export const AuditLogDrawer = ({ projectId, trigger }: Props) => {
  const [page, setPage] = useState(0)
  const query = useAuditLog(projectId, page)
  const entries = query.data ?? []
  const hasMore = entries.length === PAGE_SIZE

  return (
    <Sheet onOpenChange={(open) => !open && setPage(0)}>
      <SheetTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="gap-2">
            <History className="h-4 w-4" />
            History
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Project history</SheetTitle>
          <SheetDescription>
            Structural changes — title, visibility, publishes, and collaborator activity.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          {query.isLoading && page === 0 ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : entries.length === 0 && page === 0 ? (
            <p className="text-sm italic text-muted-foreground">No events yet.</p>
          ) : (
            <ul className="space-y-3">
              {entries.map((entry) => (
                <AuditRow key={entry.id} entry={entry} />
              ))}
            </ul>
          )}

          {(page > 0 || hasMore) && (
            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0 || query.isFetching}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="gap-1"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Newer
              </Button>
              <span className="text-xs text-muted-foreground">Page {page + 1}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasMore || query.isFetching}
                onClick={() => setPage((p) => p + 1)}
                className="gap-1"
              >
                Older
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

const AuditRow = ({ entry }: { entry: AuditLogEntry }) => (
  <li className="text-sm">
    <span className="font-medium">@{entry.actor?.handle ?? '(unknown)'}</span>{' '}
    <span className="text-muted-foreground">{verbFor(entry.eventType)}</span>
    <div className="mt-0.5 text-xs text-muted-foreground">{formatRelative(entry.createdAt)}</div>
  </li>
)
